import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction, Slice } from '@reduxjs/toolkit';
import { AgreementResponseData } from '@services/agreementApi';
import { FuelCodes } from '@utils/fuelCodes';

export interface CheckinState {
  step: number;
  user: {
    advancedCheckin: boolean;
  };
  selectedRows: string[];
  agreements: AgreementResponseData[];
  activeAgreement: AgreementResponseData;
  car: {
    licenseNumber: string;
  };
  details: {
    returnDate: number;
    pickupKilometers: number;
    returnKilometers: number;
    currentKilometers: number;
    parkingLot: string;
    receiptMailAddress: string;
    ready: boolean;
    keyInBoxCode: string;
    isFullTank: boolean;
    fuelCode: FuelCodes;
    fuelLiter?: number;
    fuelLevel?: number;
  };
  isFinished: boolean;
  touched: Record<string, boolean>;
  errors: Record<string, boolean | string>;
  resetStatus: boolean;
}

export const initialState: CheckinState = {
  step: 0,
  user: {
    advancedCheckin: false,
  },
  selectedRows: [],
  agreements: [],
  activeAgreement: null,
  car: {
    licenseNumber: '',
  },
  details: {
    returnDate: null,
    pickupKilometers: null,
    returnKilometers: null,
    currentKilometers: null,
    parkingLot: '',
    receiptMailAddress: '',
    ready: false,
    keyInBoxCode: '',
    isFullTank: true,
    fuelCode: FuelCodes.FullTank,
    fuelLiter: null,
  },
  isFinished: false,
  touched: {
    activeAgreement: false,
  },
  errors: {},
  resetStatus: false,
};

const checkinSlice: Slice<CheckinState> = createSlice({
  name: 'checkin',
  initialState,
  reducers: {
    setStep: (state, action: PayloadAction<number>) => {
      state.step = action.payload;
    },
    setAdvancedCheckin: (state, action: PayloadAction<boolean>) => {
      state.user.advancedCheckin = action.payload;
    },
    setSelectedRows: (state, action: PayloadAction<string[]>) => {
      state.selectedRows = action.payload;
    },
    setAgreements: (state, action: PayloadAction<AgreementResponseData[]>) => {
      state.agreements = action.payload;
    },
    setActiveAgreement: (
      state,
      action: PayloadAction<AgreementResponseData>
    ) => {
      state.activeAgreement = action.payload;
    },
    setLicenseNumber: (state, action: PayloadAction<string>) => {
      state.car.licenseNumber = action.payload;
    },
    setReturnDate: (state, action: PayloadAction<number>) => {
      state.details.returnDate = action.payload;
    },
    setPickupKilometers: (state, action: PayloadAction<number>) => {
      state.details.pickupKilometers = action.payload;
    },
    setReturnKilometers: (state, action: PayloadAction<number>) => {
      state.details.returnKilometers = action.payload;
    },
    setCurrentKilometers: (state, action: PayloadAction<number>) => {
      state.details.currentKilometers = action.payload;
    },
    setParkingLot: (state, action: PayloadAction<string>) => {
      state.details.parkingLot = action.payload;
    },
    setReceiptMailAddress: (state, action: PayloadAction<string>) => {
      state.details.receiptMailAddress = action.payload;
    },
    setReady: (state, action: PayloadAction<boolean>) => {
      state.details.ready = action.payload;
    },
    setKeyInBoxCode: (state, action: PayloadAction<string>) => {
      state.details.keyInBoxCode = action.payload;
    },
    setIsFullTank: (state, action: PayloadAction<boolean>) => {
      state.details.isFullTank = action.payload;
    },
    setFuelCode: (state, action: PayloadAction<FuelCodes>) => {
      state.details.fuelCode = action.payload;
    },
    setFuelLiter: (state, action: PayloadAction<number>) => {
      state.details.fuelLiter = action.payload;
    },
    setFuelLevel: (state, action: PayloadAction<number>) => {
      state.details.fuelLevel = action.payload;
    },
    setIsFinished: (state, action: PayloadAction<boolean>) => {
      state.isFinished = action.payload;
    },
    setTouched: (state, action: PayloadAction<Record<string, boolean>>) => {
      state.touched = { ...state.touched, ...action.payload };
    },
    setErrors: (
      state,
      action: PayloadAction<Record<string, string | boolean>>
    ) => {
      state.errors = { ...state.errors, ...action.payload };
    },
    resetCheckin: (state) => {
      return {
        ...initialState,
        user: {
          ...initialState.user,
          advancedCheckin: state.user.advancedCheckin,
        },

        resetStatus: !state.resetStatus,
      };
    },
  },
});

export const {
  setStep,
  setAdvancedCheckin,
  setSelectedRows,
  setAgreements,
  setActiveAgreement,
  setLicenseNumber,
  setReturnDate,
  setPickupKilometers,
  setReturnKilometers,
  setCurrentKilometers,
  setParkingLot,
  setReceiptMailAddress,
  setReady,
  setKeyInBoxCode,
  setIsFullTank,
  setFuelCode,
  setFuelLiter,
  setFuelLevel,
  setIsFinished,
  setTouched,
  setErrors,
  resetCheckin,
} = checkinSlice.actions;
export default checkinSlice.reducer;
